import React from "react";
import {
  Loader,
  LoaderSizes,
  Button,
  Pagination,
  ContentSwitcher,
  Iconography,
  IconographySizes,
  Divider,
  DividerVariants,
  IconographyThemes,
  ContextualMenu,
  SearchInput,
} from "@hid-galaxy-ui/galaxy-react";
import { useNavigate } from "react-router";
import { useAppSelector, useAppDispatch } from "../../../../redux";
import SubmissionDetailsModal from "./submission-details-modal";
import DeleteModal from "../../../../common/components/DeleteModal";
import { ReactComponent as NoUserStateSVG } from "../../../../common/assets/icons/emptystateuser.svg";
import {
  getEngagementSubmissionsSagaAction,
  reviewEngagementSagaAction,
  deleteEngagementSagaAction,
} from "../../../../redux/saga/engagement-form/saga-actions";
import { EngagementSubmission } from "../../../../redux/slices/engagement-form/Types";
import { getUserListHIDSagaAction } from "../../../../redux/saga/user/saga-actions";
import moment from "moment";
import {
  useSessionCanAccessModule,
  useSessionCanEditModule,
  useSessionModuleReadOnly,
} from "../../../../hooks/useSessionPermissions";
import { SALES_ENGAGEMENT_PERMISSION_KEYS as SE_KEYS } from "../../../../utils/permissions/salesEngagementPermissionKeys";
import "./EngagementList.scss";

const EngagementList = () => {
  const [context, setContext] = React.useState<string>("Unreviewed");
  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const [engagementList, setEngagementList] = React.useState({
    reviewed: [],
    unreviewed: [],
  });
  const { engagementSubmissions, status } = useAppSelector(
    (state) => state.engagement_form
  );
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  React.useEffect(() => {
    dispatch(getEngagementSubmissionsSagaAction());
    dispatch(getUserListHIDSagaAction());
  }, []);

  React.useEffect(() => {
    const reviewedSubmissionList = engagementSubmissions.filter(
      (item) => !!item.review_status
    );
    const unreviewedSubmissionList = engagementSubmissions.filter(
      (item) => !item.review_status
    );

    setEngagementList({
      reviewed: reviewedSubmissionList,
      unreviewed: unreviewedSubmissionList,
    });
  }, [engagementSubmissions]);

  const onTabContextClickHandler = (
    e: React.KeyboardEvent | React.MouseEvent<HTMLButtonElement, MouseEvent>,
    key: string
  ) => {
    setContext(key);
  };

  const contextSwitcherData = [
    {
      key: "Unreviewed",
      label: `Unreviewed (${engagementList.unreviewed.length})`,
    },
    { key: "Reviewed", label: `Reviewed (${engagementList.reviewed.length})` },
  ];

  const handleEngage = () => {
   			 navigate("/hid-admin/engagement-form");
 			 }
       
  return (
    <div className="engagement-list-page-container">
      <h1 className="page-title">Submitted Engagement Forms</h1>
      <div className="engagement-list-container">
        {status.engagementSubmissionsLoading ? (
          <div className="loading">
            <Loader variant={LoaderSizes.large} />
          </div>
        ) : (
          <>
            <div className="el-section-header">

            <div className="switcher-row">
              <ContentSwitcher
                data={contextSwitcherData}
                selected={context}
                onClick={onTabContextClickHandler}
              />
            </div>

            <div className="el-actions">
              <Button
              variant="primary"
              label="Create Engagment Form"
              icon={<Iconography icon="plus" />}
              onClick={handleEngage}
            />
            </div>
          </div>
            <div className="search-wrapper">
              <SearchInput
                theme="dark"
                id="searchEngagement"
                name="searchEngagement"
                placeholder="Search by Partner Name or Submitted By"
                isDisplayClearIcon={true}
                value={searchTerm}
                onChange={(event: any) =>
                  setSearchTerm(event.currentTarget.value)
                }
                onSearch={(event, value) => {
                  setSearchTerm(value);
                }}
              />
            </div>
            {context === "Unreviewed" ? (
              <PaginatedList
                engagementList={engagementList.unreviewed}
                searchTerm={searchTerm}
              />
            ) : (
              <PaginatedList
                engagementList={engagementList.reviewed}
                searchTerm={searchTerm}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

const EngagementCard: React.FC<{ engagement: EngagementSubmission }> = ({
  engagement,
}) => {
  const [submissionDetailOpen, setSubmissionDetailOpen] =
    React.useState<boolean>(false);
  const [isEditable, setIsEditable] = React.useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = React.useState<boolean>(false);
  const { userListHID } = useAppSelector((state) => state.user);
  const { status } = useAppSelector((state) => state.engagement_form);
  const dispatch = useAppDispatch();
  const manageEngagementsAccess = useSessionCanAccessModule(
    SE_KEYS.MANAGE_ENGAGEMENTS
  );
  const manageEngagementsEdit = useSessionCanEditModule(
    SE_KEYS.MANAGE_ENGAGEMENTS
  );
  const manageEngagementsReadOnly = useSessionModuleReadOnly(
    SE_KEYS.MANAGE_ENGAGEMENTS
  );
  const submitEngagementAccess = useSessionCanAccessModule(
    SE_KEYS.SUBMIT_ENGAGEMENT_FORM
  );
  const submitEngagementEdit = useSessionCanEditModule(
    SE_KEYS.SUBMIT_ENGAGEMENT_FORM
  );

  const findUser = userListHID.find(
    (user) => user.ID === engagement.submitted_by
  );
  const submittedByUserEmail = findUser ? findUser.email : "NA";
  const partnerName = engagement.data?.find(
    (field) => field.field_key === "partner_name"
  )?.value;
  const isReviewed = !!engagement.review_status;

  const handleReviewClick = () => {
    if (!manageEngagementsAccess) return;
    setIsEditable(true);
    setSubmissionDetailOpen(true);
  };

  const handleViewClick = () => {
    setIsEditable(false);
    setSubmissionDetailOpen(true);
  };

  const handleConfirmClick = () => {
    if (!manageEngagementsEdit) return;
    dispatch(
      reviewEngagementSagaAction({
        engagementId: String(engagement.id),
        review_status: 1,
      })
    );
  };

  const contextualMenuData = [
    {
      data: [
        {
          key: "mark-unreviewed",
          value: "Mark as Unreviewed",
        },
        {
          key: "delete-submission",
          value: "Delete Submission",
        },
      ],
    },
  ];

  const handleMenuItem = (event: any, item: any) => {
    if (!manageEngagementsEdit) return;
    if (item.key === "mark-unreviewed") {
      dispatch(
        reviewEngagementSagaAction({
          engagementId: String(engagement.id),
          review_status: 0,
        })
      );
    } else if (item.key === "delete-submission") {
      setDeleteModalOpen(true);
    }
  };

  const onDeleteButtonClick = () => {
    dispatch(
      deleteEngagementSagaAction({
        engagementId: String(engagement.id),
        setModalOpen: setDeleteModalOpen,
      })
    );
  };

  const confirmButtonLoading = status.reviewLoading[String(engagement.id)];
  const deleteButtonLoading = status.deleteLoading[String(engagement.id)];

  return (
    <div className="engagement-card">
      <div className="icon-info">
        {isReviewed ? (
          <Iconography
            icon="fileCheck"
            size={IconographySizes.Medium}
            theme={IconographyThemes.Success}
          />
        ) : (
          <Iconography
            className="custom-svg"
            icon="fileImport"
            size={IconographySizes.Medium}
          />
        )}
        <div className="info">
          <div className="partner-name">{partnerName}</div>
          <div className="detail">
            <span>{`Submitted by ${submittedByUserEmail}`}</span>
            <Divider mode={DividerVariants.Content} />
            <span>
              {moment(engagement.submitted_at).format("DD MMMM YYYY, HH:mm")}
            </span>
          </div>
        </div>
      </div>
      <div className="button-group">
        {!isReviewed ? (
          <>
            <Button
              variant="primary"
              label="Review"
              onClick={handleReviewClick}
            />
            <div className="confirm-button-container">
              <Button
                variant="secondary"
                label="Confirm"
                disabled={
                  manageEngagementsReadOnly || confirmButtonLoading
                }
                onClick={handleConfirmClick}
              />
              {confirmButtonLoading && (
                <Loader variant={LoaderSizes.small} />
              )}
            </div>
          </>
        ) : (
          <>
            <Button
              variant="primary"
              label="View"
              icon={<Iconography icon="bookOpenReader" />}
              onClick={handleViewClick}
            />
            {manageEngagementsReadOnly ? (
              <Button
                variant="secondary"
                icon={<Iconography icon="circleEllipsis" />}
                disabled
              />
            ) : (
              <ContextualMenu
                contextualMenuData={contextualMenuData}
                onClick={handleMenuItem}
                isToogle={true}
                placement={"bottom-end"}
              >
                <Button
                  variant="secondary"
                  icon={<Iconography icon="circleEllipsis" />}
                />
              </ContextualMenu>
            )}
          </>
        )}
      </div>
      {submissionDetailOpen && (
        <SubmissionDetailsModal
          submissionDetails={engagement}
          setModalOpen={setSubmissionDetailOpen}
          isEditable={isEditable}
          showEditButton={isEditable && submitEngagementAccess}
          canEditSubmission={submitEngagementEdit}
        />
      )}
      {deleteModalOpen && (
        <DeleteModal
          setDeleteOpen={setDeleteModalOpen}
          onDeleteButtonClick={onDeleteButtonClick}
          deleteButtonLoading={deleteButtonLoading}
          optionalHeader="Delete Engagement Submission"
          info={{
            icon: "buildings",
            name: partnerName,
          }}
          message="Are you sure you want to delete this engagement submission? This action cannot be undone."
        />
      )}
    </div>
  );
};

const PaginatedList: React.FC<{
  engagementList: EngagementSubmission[];
  searchTerm: string;
}> = ({ engagementList, searchTerm }) => {
  const [currentPageNumber, setCurrentPageNumber] = React.useState<number>(1);
  const [pageSize, setPageSize] = React.useState<number>(20);
  const { loggedUserDetail } = useAppSelector((state) => state.auth);
  const { userListHID } = useAppSelector((state) => state.user);

  // Filter and sort the engagement list
  const filteredAndSortedEngagementList = React.useMemo(() => {
    let filteredList = [...engagementList];

    // Apply search filter
    if (searchTerm.length > 0) {
      filteredList = filteredList.filter((engagement) => {
        // Get partner name from engagement data
        const partnerName =
          engagement.data?.find((field) => field.field_key === "partner_name")
            ?.value || "";

        // Get submitted by user name
        const submittedByUser = userListHID.find(
          (user) => user.ID === engagement.submitted_by
        );
        const submittedByUserName = submittedByUser
          ? `${submittedByUser.first_name} ${submittedByUser.last_name}`.toLowerCase()
          : "";
        const submittedByUserEmail =
          submittedByUser?.email?.toLowerCase() || "";

        const searchTermLower = searchTerm.toLowerCase();

        return (
          partnerName.toLowerCase().includes(searchTermLower) ||
          submittedByUserName.includes(searchTermLower) ||
          submittedByUserEmail.includes(searchTermLower)
        );
      });
    }

    // Sort the filtered list to show logged user's entries first for unreviewed forms only
    return filteredList.sort((a, b) => {
      // Only apply user-based sorting if both entries are unreviewed
      // For reviewed entries, maintain original chronological order
      if (!a.review_status && !b.review_status) {
        const aIsLoggedUser = a.submitted_by === loggedUserDetail?.ID;
        const bIsLoggedUser = b.submitted_by === loggedUserDetail?.ID;

        if (aIsLoggedUser && !bIsLoggedUser) return -1;
        if (!aIsLoggedUser && bIsLoggedUser) return 1;
      }

      // For reviewed entries or when both are from same user type, maintain original order
      return 0;
    });
  }, [engagementList, searchTerm, userListHID, loggedUserDetail?.ID]);

  // Reset to first page when search term changes
  React.useEffect(() => {
    setCurrentPageNumber(1);
  }, [searchTerm]);

  const totalRecords = filteredAndSortedEngagementList.length;
  const handlePageChange = (clickedPageNumber: number | null) => {
    setCurrentPageNumber(clickedPageNumber);
  };

  const onPageSizesClick = (e: any, value: any) => {
    setPageSize(Number(value));
    setCurrentPageNumber(1);
  };

  const paginatedData = filteredAndSortedEngagementList.slice(
    (currentPageNumber - 1) * pageSize,
    currentPageNumber * pageSize
  );
  return (
    <>
      {totalRecords > 0 ? (
        <div className="engagement-list">
          <div className="list-wrapper">
            {paginatedData.map((engagement) => {
              return (
                <EngagementCard engagement={engagement} key={engagement.id} />
              );
            })}
          </div>
          <Pagination
            currentPageNumber={currentPageNumber}
            totalRecords={totalRecords}
            pageSize={pageSize}
            border={true}
            onPageNumberClick={handlePageChange}
            onPageSizesClick={onPageSizesClick}
            pageSizes={[
              { label: "20", value: "20" },
              { label: "50", value: "50" },
              { label: "100", value: "100" },
            ]}
          />
        </div>
      ) : (
        <div className="no-engagement-submission">
          <div className="no-engagement-icon-wrapper">
            <NoUserStateSVG />
          </div>
          <p>No Engagement forms</p>
        </div>
      )}
    </>
  );
};

export default EngagementList;
